import React, { useState, useEffect } from 'react'
import {Link} from 'gatsby'
import _ from 'lodash'

import {
  EuiSpacer,
  EuiHeader,
  EuiIcon,
  EuiHeaderSectionItemButton,
  EuiCollapsibleNav,
  EuiCollapsibleNavGroup,
  EuiPinnableListGroup,
  EuiListGroupItem,
  EuiPinnableListGroupItemProps,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiShowFor,
} from '@elastic/eui'

import {
  TopLinks,
  SupportLinks,
  ProductsLinks,
  InstallationLinks,
  MotionLinks,
  InternetLinks,
  AdvancedLinks
} from './sidenav_collapsible_links'

import {
  ShopIntroduction,
  ForumIntroduction,
} from './collapsable_nav_list'

import HeaderUpdates from './header_updates'
import { SideNavItems } from './sidenav_items'

export const ThemeContext = React.createContext('dark');

if (localStorage.getItem('theme') === 'dark') {
  require('../../../themes/theme_dark.scss');
} else {
  require('../../../themes/theme_light.scss');
}

export const Chrome = ({ children }: any) => {
  const initialTheme =
    localStorage.getItem('theme') === 'dark' ? 'dark' : 'light'
  const [themeIsLoading, setThemeIsLoading] = useState(false)
  const [theme, setTheme] = useState(initialTheme)
  document.body.classList.add('euiBody--headerIsFixed--double')

  useEffect(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    return () => {
      // Clean up the subscription
      localStorage.setItem('theme', newTheme)
      setThemeIsLoading(true);
      window.location.reload();
    };
  }, [theme])

  const [navIsOpen, setNavIsOpen] = useState(
    // JSON.parse(String(localStorage.getItem('navIsDocked'))) || true
    false
  )

  const [navIsDocked, setNavIsDocked] = useState(
    JSON.parse(String(localStorage.getItem('navIsDocked'))) || true
  )

  /**
   * Accordion toggling
   */
  const [openGroups, setOpenGroups] = useState(
    JSON.parse(String(localStorage.getItem('openNavGroups'))) || [
      'Support',
      'Products',
      'Installation',
      'Motion',
      'Internet',
      'Advanced'
    ]
  );

  const toggleAccordion = (isOpen: boolean, title?: string) => {
    if (!title) return;
    const itExists = openGroups.includes(title);
    if (isOpen) {
      if (itExists) return;
      openGroups.push(title);
    } else {
      const index = openGroups.indexOf(title);
      if (index > -1) {
        openGroups.splice(index, 1);
      }
    }
    setOpenGroups([...openGroups]);
    localStorage.setItem('openNavGroups', JSON.stringify(openGroups));
  };
  /** Pinning */ const [pinnedItems, setPinnedItems] = useState<
    EuiPinnableListGroupItemProps[]
  >(JSON.parse(String(localStorage.getItem('pinnedItems'))) || []);
  const addPin = (item: any) => {
    if (!item || _.find(pinnedItems, { label: item.label })) {
      return;
    }
    item.pinned = true;
    const newPinnedItems = pinnedItems ? pinnedItems.concat(item) : [item];
    setPinnedItems(newPinnedItems);
    localStorage.setItem('pinnedItems', JSON.stringify(newPinnedItems));
  };

  const removePin = (item: any) => {
    const pinIndex = _.findIndex(pinnedItems, { label: item.label });
    if (pinIndex > -1) {
      item.pinned = false;
      const newPinnedItems = pinnedItems;
      newPinnedItems.splice(pinIndex, 1);
      setPinnedItems([...newPinnedItems]);
      localStorage.setItem('pinnedItems', JSON.stringify(newPinnedItems));
    }
  };

  function alterLinksWithCurrentState(
    links: EuiPinnableListGroupItemProps[],
    showPinned = false
  ): EuiPinnableListGroupItemProps[] {
    return links.map(link => {
      const { pinned, ...rest } = link;
      return {
        pinned: showPinned ? pinned : false,
        ...rest,
      };
    });
  }

  function addLinkNameToPinTitle(listItem: EuiPinnableListGroupItemProps) {
    return `Pin ${listItem.label} to top`;
  }

  function addLinkNameToUnpinTitle(listItem: EuiPinnableListGroupItemProps) {
    return `Unpin ${listItem.label}`;
  }

  const collapsibleNav = (
    <EuiCollapsibleNav
      id="guideCollapsibleNavAllExampleNav"
      aria-label="Main navigation"
      isOpen={navIsOpen}
      isDocked={navIsDocked}
      button={
        <EuiHeaderSectionItemButton
          aria-label="Toggle main navigation"
          onClick={() => setNavIsOpen(!navIsOpen)}>
          <EuiIcon type={'menu'} size="m" aria-hidden="true" />
        </EuiHeaderSectionItemButton>
      }
      onClose={() => setNavIsOpen(false)}>
      {/* Dark deployments section */}
      <EuiFlexItem grow={true} style={{ flexShrink: 0 }}>
        {ShopIntroduction}
      </EuiFlexItem>

      {/* Shaded pinned section always with a home item */}
      <EuiFlexItem grow={true} style={{ flexShrink: 0 }}>
        <EuiCollapsibleNavGroup
          background="light"
          className="eui-yScroll"
          style={{ maxHeight: '40vh' }}>
          <EuiPinnableListGroup
            aria-label="Pinned links" // A11y : Since this group doesn't have a visible `title` it should be provided an accessible description
            listItems={alterLinksWithCurrentState(TopLinks).concat(
              alterLinksWithCurrentState(pinnedItems, true)
            )}
            unpinTitle={addLinkNameToUnpinTitle}
            onPinClick={removePin}
            maxWidth="none"
            color="text"
            gutterSize="none"
            size="s"
          />
        </EuiCollapsibleNavGroup>
      </EuiFlexItem>

      <EuiHorizontalRule margin="none" />

      {/* BOTTOM */}
      <EuiFlexItem className="eui-yScroll">

        {/* Support section */}
        <EuiCollapsibleNavGroup
          title="Support"
          iconType="devToolsApp"
          isCollapsible={true}
          initialIsOpen={openGroups.includes('Support')}
          onToggle={(isOpen: boolean) => toggleAccordion(isOpen, 'Support')}>
          <EuiPinnableListGroup
            aria-label="Support" // A11y : EuiCollapsibleNavGroup can't correctly pass the `title` as the `aria-label` to the right HTML element, so it must be added manually
            listItems={alterLinksWithCurrentState(SupportLinks)}
            pinTitle={addLinkNameToPinTitle}
            onPinClick={addPin}
            maxWidth="none"
            color="subdued"
            gutterSize="none"
            size="s"
          />
        </EuiCollapsibleNavGroup>

        {/* Security callout */}
        {ForumIntroduction}
        
        {/* Products section */}
        <EuiCollapsibleNavGroup
          title="Products"
          iconType="spacesApp"
          isCollapsible={true}
          initialIsOpen={openGroups.includes('Products')}
          onToggle={(isOpen: boolean) => toggleAccordion(isOpen, 'Products')}>
          <EuiPinnableListGroup
            aria-label="Products" // A11y : EuiCollapsibleNavGroup can't correctly pass the `title` as the `aria-label` to the right HTML element, so it must be added manually
            listItems={alterLinksWithCurrentState(ProductsLinks)}
            pinTitle={addLinkNameToPinTitle}
            onPinClick={addPin}
            maxWidth="none"
            color="subdued"
            gutterSize="none"
            size="s"
          />
        </EuiCollapsibleNavGroup>

        {/* Installation section */}
        <EuiCollapsibleNavGroup
          title="Installation"
          iconType="managementApp"
          isCollapsible={true}
          initialIsOpen={openGroups.includes('Installation')}
          onToggle={(isOpen: boolean) => toggleAccordion(isOpen, 'Installation')}>
          <EuiPinnableListGroup
            aria-label="Installation" // A11y : EuiCollapsibleNavGroup can't correctly pass the `title` as the `aria-label` to the right HTML element, so it must be added manually
            listItems={alterLinksWithCurrentState(InstallationLinks)}
            pinTitle={addLinkNameToPinTitle}
            onPinClick={addPin}
            maxWidth="none"
            color="subdued"
            gutterSize="none"
            size="s"
          />
        </EuiCollapsibleNavGroup>

        {/* Motion section */}
        <EuiCollapsibleNavGroup
          title="Motion Detection"
          iconType="grokApp"
          isCollapsible={true}
          initialIsOpen={openGroups.includes('Motion')}
          onToggle={(isOpen: boolean) => toggleAccordion(isOpen, 'Motion')}>
          <EuiPinnableListGroup
            aria-label="Motion Detection" // A11y : EuiCollapsibleNavGroup can't correctly pass the `title` as the `aria-label` to the right HTML element, so it must be added manually
            listItems={alterLinksWithCurrentState(MotionLinks)}
            pinTitle={addLinkNameToPinTitle}
            onPinClick={addPin}
            maxWidth="none"
            color="subdued"
            gutterSize="none"
            size="s"
          />
        </EuiCollapsibleNavGroup>
        

        {/* Motion section */}
        <EuiCollapsibleNavGroup
          title="Remote Access"
          iconType="outlierDetectionJob"
          isCollapsible={true}
          initialIsOpen={openGroups.includes('Internet')}
          onToggle={(isOpen: boolean) => toggleAccordion(isOpen, 'Internet')}>
          <EuiPinnableListGroup
            aria-label="Remote Access" // A11y : EuiCollapsibleNavGroup can't correctly pass the `title` as the `aria-label` to the right HTML element, so it must be added manually
            listItems={alterLinksWithCurrentState(InternetLinks)}
            pinTitle={addLinkNameToPinTitle}
            onPinClick={addPin}
            maxWidth="none"
            color="subdued"
            gutterSize="none"
            size="s"
          />
        </EuiCollapsibleNavGroup>

        {/* Motion section */}
        <EuiCollapsibleNavGroup
          title="For Developers"
          iconType="consoleApp"
          isCollapsible={true}
          initialIsOpen={openGroups.includes('Advanced')}
          onToggle={(isOpen: boolean) => toggleAccordion(isOpen, 'Advanced')}>
          <EuiPinnableListGroup
            aria-label="For Developers" // A11y : EuiCollapsibleNavGroup can't correctly pass the `title` as the `aria-label` to the right HTML element, so it must be added manually
            listItems={alterLinksWithCurrentState(AdvancedLinks)}
            pinTitle={addLinkNameToPinTitle}
            onPinClick={addPin}
            maxWidth="none"
            color="subdued"
            gutterSize="none"
            size="s"
          />
        </EuiCollapsibleNavGroup>
        
        {/* Docking button only for larger screens that can support it*/}
        <EuiShowFor sizes={['l', 'xl']}>
          <EuiCollapsibleNavGroup>
            <EuiListGroupItem
              size="xs"
              color="subdued"
              label={`${navIsDocked ? 'Undock' : 'Dock'} navigation`}
              onClick={() => {
                setNavIsDocked(!navIsDocked);
                localStorage.setItem(
                  'navIsDocked',
                  JSON.stringify(!navIsDocked)
                );
              }}
              iconType={navIsDocked ? 'lock' : 'lockOpen'}
            />
          </EuiCollapsibleNavGroup>
        </EuiShowFor>
      </EuiFlexItem>
    </EuiCollapsibleNav>
  );

  const headers = (
    <>
      <EuiHeader
        theme="dark"
        position="fixed"
        sections={[
          {
            items: [
              <Link to="/">
                <img alt="INSTAR Deutschland GmbH" width="162" height="51" style={{marginLeft: '-18px', padding: '3px', marginTop: '5px'}} src="data:image/svg+xml;base64,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"></img>
              </Link>
            ],
            borders: 'none',
          },
          {
            items: [
              <SideNavItems />
            ],
            borders: 'none',
          },
        ]}
      />
      <EuiHeader
        position="fixed"
        sections={[
          {
            items: [collapsibleNav],
            borders: 'right',
          },
          {
            items: [<HeaderUpdates />],
            borders: 'none',
          },
        ]}
      />
    </>
  );

  return (
    <ThemeContext.Provider value={theme}>
      <EuiSpacer />
      <EuiSpacer />
      {headers}
      {children}
    </ThemeContext.Provider>
  );
};
